import { Button, Pagination, Table, Tag, Typography } from 'antd';
import BaseButton from 'components/atoms/baseButton';
import PlusIcon from 'components/atoms/icons/plus';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { DateFormats } from 'infrastructure/enums';
import { DateFormatter } from 'infrastructure/functions';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useUser from 'utils/useUser';

import CreatePapCaseModal from './create-pap-case-modal';
import PapCaseDetailsModal from './pap-case-details-modal';
import s from './styles.module.scss';
import { useCreatePapCase } from '../../hooks/use-pap-cases';
import { getStatusLabel, isEditablePapCase } from '../../hooks/helper';

import type { PapCase, PapCasesSortBy } from '../../types';
import type { Patient } from 'infrastructure/classes/patient/patient';
import type { TableColumnsType } from 'antd';

const { Text } = Typography;

interface PapCaseListProps {
  items: PapCase[];
  loading?: boolean;
  onDownloadPapForm?: (
    papFormGuid: string,
    papCaseGuid?: string,
  ) => Promise<string>;
  page?: number;
  total?: number;
  onPageChange?: (nextPage: number) => void;
  onSortChange?: (
    sortBy?: 'updatedAt' | 'status',
    sortOrder?: 'ASC' | 'DESC',
  ) => void;
  patientGuid: string;
  patientInfo?: Patient;
}

const PapCaseList: FC<PapCaseListProps> = ({
  items: papCases,
  loading = false,
  onDownloadPapForm,
  page = 1,
  total = 0,
  onPageChange,
  onSortChange,
  patientGuid,
  patientInfo,
}) => {
  const { t } = useTranslation();
  const { fullName, userType, roleNames } = useUser();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectingCase, setSelectingCase] = useState<PapCase | null>(null);

  const canCreateOrEditPapCase = isEditablePapCase(userType, roleNames);

  const { mutateAsync: createPapCase, isLoading: isCreating } =
    useCreatePapCase(patientGuid);

  const onOpenCaseDetail = (record: PapCase) => {
    setSelectingCase(record);
  };

  const columns = useMemo<TableColumnsType<PapCase>>(
    () => [
      {
        title: t('labels.papCaseName'),
        dataIndex: 'name',
        key: 'caseName',
        width: '28.5rem',
        ellipsis: { showTitle: true },
        render: (value: string, record: PapCase) => (
          <Button
            variant="text"
            className={s['case-link']}
            onClick={() => onOpenCaseDetail(record)}
          >
            {value}
          </Button>
        ),
      },
      {
        title: t('labels.lastUpdate'),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: '9.5rem',
        sorter: true,
        render: (value: string) =>
          value
            ? DateFormatter({ date: value, format: DateFormats['MM/dd/yyyy'] })
            : '-',
      },
      {
        title: t('labels.papFormVersion'),
        key: 'version',
        width: '11rem',
        render: (_: unknown, record: PapCase) => {
          const version =
            record.latestFormVersion ?? record.latestForm?.version;
          return version != null ? `v${version}` : '-';
        },
      },
      {
        title: t('labels.status'),
        dataIndex: 'status',
        key: 'status',
        width: '7.5rem',
        sorter: true,
        render: (value: string) => (
          <Tag className={s[`status-${value}`]}>{getStatusLabel(value, t)}</Tag>
        ),
      },
      {
        title: t('labels.reviewerNotes'),
        dataIndex: 'note',
        key: 'notes',
        width: '28.5rem',
        render: (_: string, record: PapCase) =>
          record?.latestReview?.note || (
            <Text className={s['no-note-text']} type="secondary">
              {t('labels.noNote')}
            </Text>
          ),
      },
    ],
    [onOpenCaseDetail, t],
  );

  const onTableChange = (
    _pagination: any,
    _filters: any,
    sorter: any,
    extra: any,
  ) => {
    if (extra?.action !== 'sort') return;

    if (sorter?.order && sorter?.field) {
      onSortChange?.(
        sorter.field as PapCasesSortBy,
        sorter.order === 'descend' ? 'DESC' : 'ASC',
      );
    } else {
      onSortChange?.();
    }
  };

  return (
    <div className={s.wrapper}>
      <div className={s.header}>
        <h5 className={s.title}>{t('labels.prescriptionAssistanceProgram')}</h5>
        <BaseButton
          icon={<PlusIcon color="transparent" />}
          label={t('labels.newPapCase')}
          onClick={() => setIsCreateOpen(true)}
          disabled={!canCreateOrEditPapCase}
        />
      </div>
      <div className={s['table-wrapper']}>
        <Table
          rowKey="guid"
          columns={columns}
          dataSource={papCases}
          loading={loading}
          pagination={false}
          onChange={onTableChange}
          locale={{ emptyText: t('papCase.noPapCasesCreated') }}
          sortDirections={['descend', 'ascend']}
        />
      </div>
      <div className={s.pagination}>
        <Pagination
          current={page}
          total={total}
          pageSize={5}
          showSizeChanger={false}
          onChange={onPageChange}
        />
      </div>

      <CreatePapCaseModal
        open={isCreateOpen}
        creating={isCreating}
        existingCaseNames={papCases.map((item) => item.name)}
        patientName={
          patientInfo?.loginInfo?.fullName ?? t('papCase.notAvailable')
        }
        patientDob={patientInfo?.loginInfo?.birthDate}
        ptId={patientInfo?.slhId ?? patientGuid}
        createdBy={fullName}
        onCancel={() => setIsCreateOpen(false)}
        onCreate={async ({ caseName, specialist }) => {
          if (onPageChange && page !== 1) {
            onPageChange(1);
          }

          const createdCase = await createPapCase({
            name: caseName,
            isSpecialist: specialist,
          });

          setIsCreateOpen(false);
          setSelectingCase(createdCase);
          notificationController.success({
            message: t('papCase.papCaseCreatedSuccessfully'),
          });
        }}
      />

      <PapCaseDetailsModal
        open={!!selectingCase}
        patientGuid={patientGuid}
        papCaseGuid={selectingCase?.guid || ''}
        patientInfo={patientInfo}
        onDownloadPapForm={onDownloadPapForm}
        onClose={() => {
          setSelectingCase(null);
        }}
        isReadOnly={!canCreateOrEditPapCase}
      />
    </div>
  );
};

export default PapCaseList;
